import type { PlaylistVideo, VideoSeries } from '@/types'

import { videoSeriesList } from '@/constants/video-playlist'

/**
 * Every video lesson has a page of its own: /videoleksione/<series anchor>/<lesson slug>.
 * Slugs come from the lesson titles (accents and apostrophes dropped), kept unique per series.
 * Safe to use on the client.
 */

export interface VideoLesson {
    video: PlaylistVideo
    slug: string
    /** Position in the series, 0-based (the promo, when there is one, is 0). */
    index: number
    /** Lesson number shown to visitors, or null for the promo video. */
    number: number | null
    isPromo: boolean
}

const toSlug = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        // The Ӑ in ‘Ӑjn’ is written with a Cyrillic letter.
        .replace(/[\u0410\u0430]/g, 'a')
        .toLowerCase()
        .replace(/[\u2019'`\u02bc]/g, '')
        .replace(/&/g, ' ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

const cache = new Map<string, VideoLesson[]>()

export const lessonsOf = (series: VideoSeries): VideoLesson[] => {
    const cached = cache.get(series.id)
    if (cached) return cached

    const used = new Set<string>()
    const lessons = series.videos.map((video, index) => {
        const isPromo = series.hasPromo && index === 0
        const base = (isPromo ? 'promo' : toSlug(video.title)) || video.id.toLowerCase()
        let slug = base
        for (let n = 2; used.has(slug); n += 1) slug = `${base}-${n}`
        used.add(slug)
        return {
            video,
            slug,
            index,
            number: isPromo ? null : series.hasPromo ? index : index + 1,
            isPromo
        }
    })
    cache.set(series.id, lessons)
    return lessons
}

export const lessonPath = (series: VideoSeries, lesson: VideoLesson): string =>
    `/videoleksione/${series.anchor}/${lesson.slug}`

/** The page of one video, found by its YouTube id. */
export const lessonPathForVideo = (series: VideoSeries, videoId: string): string => {
    const lesson = lessonsOf(series).find((item) => item.video.id === videoId)
    return lesson ? lessonPath(series, lesson) : `/videoleksione#${series.anchor}`
}

export const findLesson = (
    anchor: string,
    slug: string
): { series: VideoSeries; lesson: VideoLesson; lessons: VideoLesson[] } | null => {
    const series = videoSeriesList.find((item) => item.anchor === anchor)
    if (!series) return null
    const lessons = lessonsOf(series)
    const lesson = lessons.find((item) => item.slug === slug)
    return lesson ? { series, lesson, lessons } : null
}

export const allLessonPaths = (): string[] =>
    videoSeriesList.flatMap((series) => lessonsOf(series).map((lesson) => lessonPath(series, lesson)))
